// Lead-creation helpers using Firestore.
// Public site is allowed to WRITE to these collections (no auth needed)
// per the security rules.
import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  limit as fbLimit,
  getDocs,
} from "firebase/firestore";
import { db } from "./firebase";

// Allowed enums — mirror what the UI dropdowns expose.
const PRODUCT_OPTIONS = new Set([
  "Green Cardamom",
  "Almonds",
  "Cashews",
  "Pistachios",
  "Raisins",
  "Dates",
  "Multiple Products",
]);
const QUANTITY_OPTIONS = new Set([
  "Below 10 KG",
  "10 KG - 50 KG",
  "50 KG - 100 KG",
  "100 KG - 500 KG",
  "500 KG+",
]);
const MOBILE_RE = /^[6-9]\d{9}$|^\+?\d{10,15}$/;

const stripPhone = (v) => (v || "").toString().replace(/\s|-/g, "");
const validatePhone = (v) => {
  const cleaned = stripPhone(v);
  if (!MOBILE_RE.test(cleaned)) throw new Error("Invalid phone number");
  return cleaned;
};

const validateNonEmpty = (v, label) => {
  if (!v || v.toString().trim().length < 2)
    throw new Error(`${label} is required`);
  return v.toString().trim();
};

// 1) Primary CTA popup (the new wholesale-quote form)
export async function createWholesaleEnquiry(payload) {
  const data = {
    name: validateNonEmpty(payload.name, "Name"),
    phone: validatePhone(payload.phone),
    email: (payload.email || "").trim() || null,
    product: payload.product,
    quantity: payload.quantity,
    additional_requirements:
      (payload.additional_requirements || "").trim() || null,
    source: "wholesale_quote_popup",
    created_at: serverTimestamp(),
  };
  if (!PRODUCT_OPTIONS.has(data.product))
    throw new Error("Please select a valid product");
  if (!QUANTITY_OPTIONS.has(data.quantity))
    throw new Error("Please select a valid quantity range");
  const ref = await addDoc(collection(db, "wholesale_enquiries"), data);
  return { id: ref.id, ...data };
}

// 2) Legacy inline enquiry form (still rendered on the page)
export async function createEnquiry(payload) {
  const data = {
    name: validateNonEmpty(payload.name, "Name"),
    mobile: validatePhone(payload.mobile),
    business_name: validateNonEmpty(payload.business_name, "Business name"),
    location: validateNonEmpty(payload.location, "Location"),
    requirement: validateNonEmpty(payload.requirement, "Requirement"),
    quantity: validateNonEmpty(payload.quantity, "Quantity"),
    source: "enquiry_form",
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, "enquiries"), data);
  return { id: ref.id, ...data };
}

// 3) Timed + exit-intent mini popups
export async function createQuickLead(payload) {
  const data = {
    name: validateNonEmpty(payload.name, "Name"),
    mobile: validatePhone(payload.mobile),
    business_name: validateNonEmpty(payload.business_name, "Business name"),
    source: payload.source || "timed_popup",
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, "quick_leads"), data);
  return { id: ref.id, ...data };
}

// ---- Admin reads (require authenticated user per security rules) ----
export async function listCollection(name, max = 200) {
  const q = query(
    collection(db, name),
    orderBy("created_at", "desc"),
    fbLimit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    // Convert Firestore Timestamp → ISO string for the UI
    const created_at = data.created_at?.toDate?.()?.toISOString() || null;
    return { id: d.id, ...data, created_at };
  });
}
